<template>
    <div ref="wrapperRef" class="sp-wrapper">
        <slot 
            name="trigger" 
            :toggle="toggle" 
            :isOpen="isOpen" 
            :activeFilters="activeFilters">            
            <button type="button" class="btn btn-sm sp-trigger" :class="{ active: isOpen }" @click.stop="toggle">
                <i class="fas fa-search"></i>
                {{ title ?? 'Filtrar' }}
                <span v-if="activeFilters > 0" class="sp-badge">{{ activeFilters }}</span>
            </button>
        </slot>
        <Teleport to="body">
            <Transition name="sp-fade">
                <div v-if="isOpen" 
                    ref="dropdownRef" 
                    class="sp-dropdown" 
                    role="dialog" 
                    aria-label="Search filters"
                    :style="dropdownStyle">
                    
                    <div class="sp-header">
                        <span class="sp-label text-primary">Search filters</span>
                        <button type="button" class="sp-close" @click="close">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>

                    <div class="sp-fields">
                        <slot>

                        </slot>
                    </div>

                    <div class="sp-actions">
                        <button type="button" class="btn btn-link btn-sm" >Limpiar</button>
                        <button type="button" class="btn btn-primary btn-sm" v-on:click="onSearchClick">
                            <i class="fas fa-search"></i> Buscar
                        </button>
                    </div>
                </div>
            </Transition>
        </Teleport>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps<{
    title?: string
    initiallyOpen?: boolean
}>()

const emit = defineEmits<{
  (e: 'actionSearch'): void
}>()

const onSearchClick = () => {
  emit('actionSearch')
  close() // opcional pero recomendado
}

const isOpen = ref(props.initiallyOpen ?? false)
const text = ref('')
const date = ref('')

const wrapperRef = ref<HTMLElement | null>(null)
const dropdownRef = ref<HTMLElement | null>(null)
const dropdownStyle = ref({})

const activeFilters = computed(() => {
    let count = 0
    if (text.value?.trim()) count++
    if (date.value) count++
    return count
})


const toggle=()=> {
    isOpen.value = !isOpen.value
    if (isOpen.value) nextTick(updateDropdownPosition)
}

const close=()=> { isOpen.value = false }

const updateDropdownPosition=() => {
    if (!wrapperRef.value || !dropdownRef.value) return
    const rect = wrapperRef.value.getBoundingClientRect()
    const dropdownRect = dropdownRef.value.getBoundingClientRect()
    let left = rect.left
    if (left + dropdownRect.width > window.innerWidth - 10)
        left = window.innerWidth - dropdownRect.width - 10
    if (left < 10) left = 10
    dropdownStyle.value = {
        top: `${rect.bottom + window.scrollY + 6}px`,
        left: `${left + window.scrollX}px`,
    }
}

const handleClickOutside=(e: MouseEvent) => {
    if (!isOpen.value) return

    const target = e.target as Node

    const clickedInsideWrapper =
        wrapperRef.value?.contains(target)

    const clickedInsideDropdown =
        dropdownRef.value?.contains(target)

    if (!clickedInsideWrapper && !clickedInsideDropdown) {
        close()
    }
}

const handleKeydown=(e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen.value) close()
}

onMounted(() => {
    document.addEventListener('click', handleClickOutside)
    document.addEventListener('keydown', handleKeydown)
    window.addEventListener('scroll', updateDropdownPosition, true)
    window.addEventListener('resize', updateDropdownPosition)
})
onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
    document.removeEventListener('keydown', handleKeydown)
    window.removeEventListener('scroll', updateDropdownPosition, true)
    window.removeEventListener('resize', updateDropdownPosition)
})


</script>

<style scoped>
.sp-wrapper .btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 31px;
}

.sp-wrapper .btn i {
  font-size: 12px;
}

.sp-trigger {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    color: #495057;
    cursor: pointer;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.sp-trigger:hover {
    background: #e9ecef;
    border-color: #ced4da;
}

.sp-trigger.active {
    background: #e7f1ff;
    border-color: #86b7fe;
    color: #0d6efd;
}

.sp-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #0d6efd;
    color: #fff;
    border-radius: 20px;
    font-size: 11px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    font-weight: 600;
    margin-left: 4px;
    line-height: 1;
}


.sp-dropdown {
    position: absolute;
    z-index: 1050;
    min-width: 320px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 12px 28px rgba(0, 0, 0, 0.15), 0 2px 4px rgba(0, 0, 0, 0.05);
    padding: 16px;
    backdrop-filter: blur(0px);
    transition: all 0.2s ease;
}


.sp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 14px;
    padding-bottom: 8px;
    border-bottom: 1px solid #edf2f7;
}

.sp-label {
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.6px;
}

.sp-close {
    background: none;
    border: none;
    cursor: pointer;
    color: #94a3b8;
    font-size: 14px;
    padding: 4px;
    border-radius: 8px;
    line-height: 1;
    transition: all 0.15s;
}

.sp-close:hover {
    color: #1e293b;
    background: #f1f5f9;
}


.sp-fields {
    display: flex;
    flex-direction: column;
    gap: 14px;
    margin-bottom: 4px;
}

.sp-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.sp-field-label {
    font-size: 12px;
    font-weight: 500;
    color: #334155;
    letter-spacing: 0.3px;
}

.sp-input {
    padding: 8px 12px;
    font-size: 13px;
    border: 1px solid #cbd5e1;
    border-radius: 10px;
    background: #fff;
    transition: all 0.2s;
    outline: none;
}

.sp-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
}

.sp-actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid #edf2f7;
}

.sp-clear {
    background: transparent;
    border: none;
    color: #64748b;
    font-size: 12px;
    font-weight: 500;
    padding: 6px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.15s;
}

.sp-clear:hover {
    background: #f1f5f9;
    color: #1e293b;
}

.sp-apply {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #0d6efd;
    border: none;
    color: #fff;
    font-size: 12px;
    font-weight: 500;
    padding: 6px 14px;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.15s;
}

.sp-apply:hover {
    background: #0b5ed7;
    transform: translateY(-1px);
}

.sp-fade-enter-active,
.sp-fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.sp-fade-enter-from,
.sp-fade-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
</style>